import Backbone, {Model, ModelSetOptions} from 'backbone';
import _ from 'underscore';

import {assert} from 'Asserts';
import * as benchlingUtil from 'BenchlingUtil';
import {
  CodonTranslationTable,
  GENETIC_CODE_USER_PREFERENECE_KEY,
  MeltingTempMethod,
} from 'biojs/BioConstants';
import {AMINO_ACID_ABBREVIATION_PREF_KEY, AbbreviationFormat, DEFAULT_DNA_START_CODON} from 'biojs/Codons';
import {defaultGibsonParameters} from 'bulk-workflows/assembly/BulkAssemblyConstants';
import {CalendarPreferences, CalendarView} from 'calendar/CalendarConstants';
import {
  AlignmentPreferences,
  AlignmentProgram,
  CLUSTALO_DEFAULTS,
  MAFFT_DEFAULTS,
} from 'constants/AlignmentConstants';
import {GBPreferences} from 'constants/ExportConstants';
import * as FileBrowserConstants from 'constants/FileBrowserConstants';
import {MapType, SEQUENCE_MAP_TYPE_USER_PREFERENCE_KEY} from 'constants/MapsConstants';
import {FindPrimersPreferences, PrimerPreferences, RESULT_SORTS} from 'constants/PrimerConstants';
import {CopyPreferences, SequencePreferences} from 'constants/SequenceConstants';
import UserConstants from 'constants/UserConstants';
import {getNavigatorLocaleIfSupported, isLocaleSupported} from 'i18n/I18nUtils';
import {DEFAULT_LANGUAGE_TAG} from 'i18n/LanguageTagConstants';
import {isBrowser} from 'lib/Browser';
import {USED_CHAT_USE_CASES_PREFERENCE_KEY} from 'llm/chat-library/use-cases/chatUseCaseConstants';
import {DISMISSED_AUTOMATIC_FULL_ACTIONS_NOTICE_PREFERENCE} from 'llm/chat-library/VoicePreferences';
import {ScientificModelPreferences} from 'model-hub/scientific-models/ScientificModelConstants';
import {BulkAssemblyPreferences} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {
  CodonOptimizationMethod,
  CodonOptimizationPreferences,
  GCLevel,
} from 'molbio/codon-optimization/CodonOptimizationConstants';
import EnzymeConstants, {EnzymePreferences, EnzymeType} from 'molbio/enzymes/EnzymeConstants';
import {SequenceMapPreferences} from 'molbio/maps/SequenceMapConstants';
import {
  ModifiedNucleotidesPreferences,
  NUCLEOTIDES_TABLE_DEFAULT_PAGE_SIZE,
} from 'molbio/monomers/MonomerConstants';
import {HomepagePreferences} from 'nav/sidepanel/homepage/HomepageConstants';
import {
  DEFAULT_REVIEW_STATUS_LABEL,
  DEFAULT_TASK_STATUS_LABEL,
  MyWorkCategory as MyWorkCategoryV2,
} from 'nav/sidepanel/homepage/my-work-v2/MyWorkConstants';
import * as PrintConstants from 'notebook/mediocre/print/PrintConstants';
import {CoordinatesOrderingKey, GridSortOptionsPreferences} from 'samples/grids/GridCellTableConstants';
import {PlateMapPreferences} from 'samples/plate-maps/PlateMapConstants';
import {SCHEMA_DIAGRAM_COORDINATES_PREF_KEY} from 'schemas/data-model-diagram/SchemaDiagramConstants';
import {StudiesPreferences} from 'studies/StudiesPreferences';
import {UTC_TIMEZONE} from 'util/DateConstants';
import * as net from 'util/Net';

import {
  ScrollableTablePreferences,
  SortDirection,
  TABLE_KEYS,
} from './ui-library/scrollable/ScrollableTableConstants';

class Preferences extends Backbone.Model {}

const {DENSITY} = FileBrowserConstants;

const {LANGUAGE_PREFERENCE_KEY, SPREADSHEET_IMPORT_LANGUAGE_TAG} = UserConstants;

const _dataStore = new Preferences(isBrowser ? window.userprefs : {});
_dataStore.on('change', () => {
  const prefs = preferencesModule as unknown as Backbone.Events;
  for (const property of Object.keys(_dataStore.changed)) {
    prefs.trigger(`change:${property}`, preferencesModule, _dataStore.get(property));
  }
  prefs.trigger('change', preferencesModule);
});

const DEFAULTS = {
  [`${TABLE_KEYS.EXPANDED_PROJECT_FILE_TABLE}${ScrollableTablePreferences.HIDDEN_COLUMN_NAMESPACE}`]: [
    'createdAt',
  ],
  [`${TABLE_KEYS.EXPANDED_REGISTRY_FILE_TABLE}${ScrollableTablePreferences.HIDDEN_COLUMN_NAMESPACE}`]: [
    'validationErrors',
    'createdAt',
  ],
  [`${TABLE_KEYS.EXPANDED_STORABLE_TABLE}${ScrollableTablePreferences.HIDDEN_COLUMN_NAMESPACE}`]: [
    'createdAt',
  ],
  [`${TABLE_KEYS.GRID_CELL_TABLE}.${GridSortOptionsPreferences.COORDINATES_ORDER_BY_KEY}`]:
    CoordinatesOrderingKey.ROW,
  [`${TABLE_KEYS.GRID_CELL_TABLE}.${GridSortOptionsPreferences.COORDINATES_SORT_DIRECTION}`]:
    SortDirection.ASCENDING_SORT,
  [AlignmentPreferences.ALIGNMENT_PROGRAM]: AlignmentProgram.MAFFT,
  [AlignmentPreferences.CLUSTAL_O_PARAMETERS]: CLUSTALO_DEFAULTS,
  [AlignmentPreferences.DISABLE_CIRCULAR_TEMPLATE_ROTATION]: false,
  [AlignmentPreferences.MAFFT_PARAMETERS]: MAFFT_DEFAULTS,
  [AMINO_ACID_ABBREVIATION_PREF_KEY]: AbbreviationFormat.TRIPLE_LETTER,
  [BulkAssemblyPreferences.GIBSON_PARAMETERS]: defaultGibsonParameters,
  [BulkAssemblyPreferences.SHOW_FRAGMENT_DETAILS]: true,
  [BulkAssemblyPreferences.SHOW_OVERLAP_DETAILS]: true,
  [CalendarPreferences.PREFERRED_CALENDAR_VIEW]: CalendarView.MONTH_VIEW,
  [CodonOptimizationPreferences.AVOID_HAIRPINS]: false,
  [CodonOptimizationPreferences.CUTSITES]: [],
  [CodonOptimizationPreferences.GC_CONTENT]: {min: 0, max: 1},
  [CodonOptimizationPreferences.GC_LEVEL]: GCLevel.ANY,
  [CodonOptimizationPreferences.HAIRPIN_STEM]: {value: 20, error: ''},
  [CodonOptimizationPreferences.HAIRPIN_WINDOW]: {value: 200, error: ''},
  [CodonOptimizationPreferences.METHOD]: CodonOptimizationMethod.MATCH_CODON_USAGE,
  [CodonOptimizationPreferences.MODAL_ORGANISM]: null,
  [CodonOptimizationPreferences.ORGANISM]: null,
  [CodonOptimizationPreferences.REDUCE_PATTERNS]: [],
  [CodonOptimizationPreferences.URIDINE]: false,
  [CopyPreferences.ANNOTATIONS]: true,
  [CopyPreferences.ANNOTATIONS_NOT_CONTAINED]: false,
  [CopyPreferences.DESCRIPTION]: false,
  [CopyPreferences.PRIMER_BASES]: true,
  [CopyPreferences.TAGS]: true,
  [EnzymePreferences.ENZYME_LIST_ID]: EnzymeConstants.DEFAULT_ENZYMES_LIST_ID,
  [EnzymePreferences.PROVIDER]: EnzymeConstants.baseProviders[0].name,
  [EnzymePreferences.VIEW_ENZYMES]: EnzymeType.SINGLEANDDOUBLE,
  'finder.DENSITY': DENSITY.COMPACT,
  [FindPrimersPreferences.EXACT_MATCH]: false,
  [FindPrimersPreferences.MAX_MELTING_TEMP]: 100,
  [FindPrimersPreferences.MAX_MISMATCHES]: 1,
  [FindPrimersPreferences.MAX_TOTAL_MISMATCHES]: 5,
  [FindPrimersPreferences.MIN_MATCHING_BASES]: 14,
  [FindPrimersPreferences.MIN_MELTING_TEMP]: 48,
  [FindPrimersPreferences.RESULTS_SORT]: RESULT_SORTS.LOCATION,
  [FindPrimersPreferences.UNIQUE_MATCH]: false,
  [GBPreferences.CONVERT_NON_STANDARD_ANNOTATIONS]: false,
  [GBPreferences.EXPORT_ANNOTATION_COLORS]: true,
  [GENETIC_CODE_USER_PREFERENECE_KEY]: CodonTranslationTable.STANDARD,
  [HomepagePreferences.MY_WORK_SELECTED_CATEGORY_V2]: MyWorkCategoryV2.ENTRIES,
  [HomepagePreferences.MY_WORK_ENTRIES_STATUS_FILTER_V2]: DEFAULT_REVIEW_STATUS_LABEL,
  [HomepagePreferences.MY_WORK_WORKSHEETS_STATUS_FILTER_V2]: DEFAULT_REVIEW_STATUS_LABEL,
  [HomepagePreferences.MY_WORK_TASKS_STATUS_FILTER_V2]: DEFAULT_TASK_STATUS_LABEL,
  [HomepagePreferences.MY_WORK_TEMPLATES_STATUS_FILTER]: DEFAULT_REVIEW_STATUS_LABEL,
  [HomepagePreferences.HOMEPAGE_CARD_LAYOUT_V2]: null,
  [DISMISSED_AUTOMATIC_FULL_ACTIONS_NOTICE_PREFERENCE]: false,
  // If the user preference isn't set, fall back to navigator.language.
  [LANGUAGE_PREFERENCE_KEY]: benchlingUtil.navigatorLanguage,
  [ModifiedNucleotidesPreferences.HAS_DISMISSED_UT_FLEXIBILITY_BANNER]: false,
  [ModifiedNucleotidesPreferences.NUCLEOTIDES_TABLE_PAGE_SIZE]: NUCLEOTIDES_TABLE_DEFAULT_PAGE_SIZE,
  [PlateMapPreferences.HAS_DISMISSED_TOO_BIG_PLATE_BANNER]: false,
  [PrimerPreferences.MELTING_TEMP_DNA]: {
    method: MeltingTempMethod.primer3,
    dnaConc: null,
    monovalentConc: null,
    divalentConc: null,
    dntpConc: null,
  },
  [PrimerPreferences.MELTING_TEMP_RNA]: {
    method: MeltingTempMethod.xia,
    dnaConc: null,
    monovalentConc: null,
    divalentConc: null,
    dntpConc: null,
  },
  [PrimerPreferences.SECONDARY_TEMP]: 37,

  [PrintConstants.PrintPreferences.ELN_FIT_TO_WIDTH]: true,
  [PrintConstants.PrintPreferences.ELN_ORIENTATION]: PrintConstants.PrintOrientation.PORTRAIT,
  [PrintConstants.PrintPreferences.ELN_PAPER_SIZE]: PrintConstants.PaperSize.US_LETTER,
  [PrintConstants.PrintPreferences.ELN_SHOW_LINK_URLS]: true,
  [PrintConstants.PrintPreferences.LABEL_PRINTING_TIMEZONE]: UTC_TIMEZONE,
  [SCHEMA_DIAGRAM_COORDINATES_PREF_KEY]: {},
  [ScientificModelPreferences.HAS_DISMISSED_MODEL_CONFIDENCE_POPOVER]: false,
  [SEQUENCE_MAP_TYPE_USER_PREFERENCE_KEY]: MapType.LINEAR_SEQUENCE,
  [SequenceMapPreferences.ORF_START_CODON]: DEFAULT_DNA_START_CODON,
  [SequencePreferences.SHOULD_COLLAPSE_PARTS_WARNING]: false,
  [SPREADSHEET_IMPORT_LANGUAGE_TAG]: null,
  [StudiesPreferences.SHOW_FLOWCHART_PROCEDURE_RUN_DESIGN]: true,
  [USED_CHAT_USE_CASES_PREFERENCE_KEY]: [],
};

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
function prefSet(params: {[key: string]: any}, val?: unknown): Promise<any>;
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
function prefSet(key: string, val: any): Promise<any>;
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
function prefSet(key: any, val: any): Promise<any> {
  let attrs;
  if (typeof key === 'object') {
    attrs = key;
  } else {
    attrs = {[key]: val};
  }

  const changedAttrs = _.chain(attrs)
    .pairs()
    .filter(([eachKey, eachVal]) => _dataStore.get(eachKey) !== eachVal)
    .object()
    .value();

  if (Object.keys(changedAttrs).length === 0) {
    return Promise.resolve();
  }

  _dataStore.set(changedAttrs);

  // Do not update preferences if there is not a user (i.e. setting preferences from execjs)
  if (isBrowser && window.user != null) {
    return net.postJSONPromise({
      url: '/1/api/users/me/preferences',
      data: changedAttrs,
    });
  }

  return Promise.resolve();
}

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
function prefGet(key: string): any {
  assert(key != null, 'key was null');
  const value = _dataStore.get(key);
  if (value != null) {
    return value;
  }
  return DEFAULTS[key];
}

function prefPick(fn: (value: unknown, key: string, object: unknown) => boolean): Record<string, unknown> {
  return _dataStore.pick(fn);
}

function prefUnset(key: string): void {
  _dataStore.unset(key);
  // Do not update preferences if there is not a user (i.e. setting preferences from execjs)
  if (isBrowser && window.user != null) {
    net.delete_({
      url: '/1/api/users/me/preferences',
      data: {keys: [key]},
    });
  }
}

function prefResetForTests(): void {
  _dataStore.clear({silent: true});
}

export function prefInit(): void {
  const currentLanguagePreferenceKey = _dataStore.get(LANGUAGE_PREFERENCE_KEY);
  // Set language preference on page load if it isn't already set.
  if (currentLanguagePreferenceKey == null) {
    prefSet(LANGUAGE_PREFERENCE_KEY, benchlingUtil.navigatorLanguage);
  }

  if (_dataStore.get(SPREADSHEET_IMPORT_LANGUAGE_TAG) == null) {
    let defaultSpreadsheetLocale;
    if (currentLanguagePreferenceKey != null && isLocaleSupported(currentLanguagePreferenceKey)) {
      defaultSpreadsheetLocale = currentLanguagePreferenceKey;
    } else {
      defaultSpreadsheetLocale = getNavigatorLocaleIfSupported() ?? DEFAULT_LANGUAGE_TAG.code;
    }
    prefSet(SPREADSHEET_IMPORT_LANGUAGE_TAG, defaultSpreadsheetLocale);
  }
}

interface PreferenceSetOptions extends ModelSetOptions {
  noSave?: boolean;
}

class PreferenceModel extends Backbone.Model {
  // The desired behavior is for `doneInitialize` to be `false` during the duration of the constructor,
  // and then set to true at the end via initialize(), and to allow subclasses to override the default
  // values of preservedAttributes / prefix via an initClass.
  //
  // We don't set the initial value here (e.g. `private doneInitialize: boolean = false;`), because this ends
  // up behaving unexpectedly when overwritten via initialize(). As it turns out, the initial values are set
  // at the very end of the constructor, which happens /after/ initialize(), which is when the custom
  // value of the field was to be set (see the end of `initialize` implementation below).
  //
  // Relatedly, preservedAttributes and prefix are typically overwritten at the prototype level, so defining
  // a value here would blow away the desired overwrites when the model gets constructed.
  //
  // Instead, the following private / protected fields are always defined on the prototype via initClass().
  protected preservedAttributes!: Array<string>;
  protected prefix!: string;
  private doneInitialize!: boolean;

  static initClass(): void {
    // Flipped to true via initialize()
    this.prototype.doneInitialize = false;

    // Override prefix / preservedAttributes in child classes.
    this.prototype.preservedAttributes = [];
    this.prototype.prefix = 'b';
  }

  enabled(): boolean {
    return isBrowser && window.user != null;
  }

  override initialize(attributes?: {}, options?: {}): void {
    if (this.enabled()) {
      for (let i = 0; i < this.preservedAttributes.length; i++) {
        const key = this.preservedAttributes[i];
        const val = prefGet(`${this.prefix}-${key}`);
        if (val != null) {
          this.set(key, val);
        }
      }
      // doneInitialize prevents set() calls prior to initialize (i.e. from
      // inside the Model constructor) from blowing away persisted values.
      this.doneInitialize = true;
    }
  }

  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  override set(key: string, val: any, options?: PreferenceSetOptions): Model;
  override set(attrs: {[key: string]: unknown}, options?: PreferenceSetOptions): Model;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  override set(key: string | {[key: string]: unknown}, val: any, options: PreferenceSetOptions = {}): Model {
    let attrs;
    if (typeof key === 'object') {
      attrs = key;
      options = val;
    } else {
      attrs = {[key]: val};
    }
    options = _.defaults(options, {noSave: false});
    if (this.enabled() && this.doneInitialize && !options.noSave) {
      for (const [k, v] of Object.entries(attrs)) {
        if (this.preservedAttributes.includes(k)) {
          prefSet(`${this.prefix}-${k}`, v);
        }
      }
    }
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    return super.set(key as any, val, options);
  }
}
PreferenceModel.initClass();

const preferencesModule = {
  init: prefInit,
  get: prefGet,
  pick: prefPick,
  set: prefSet,
  unset: prefUnset,
  resetForTests: prefResetForTests,
  PreferenceModel,
  ...Backbone.Events,
};

export default preferencesModule;
